<template>
  <div class="group-vitality-tile" :class="elClasses">

    <!-- Main content always displayed -->
    <div class="main-tile">
      <!-- Main tile content -->
      <div class="tile-wrapper">
        <div class="group-name">
          <EditableLabel
            :key="nameKey"
            :value="tileTitle"
            :disabled="isCategory"
            :placeholder="$t('message.VH_PH_GROUP_NAME')"
            ellipsize
            @input="$emit('nameEdit', $event)"
          />

          <div class="group-teams" @click="$emit('teamsClick')">
            {{ groupTeams }}
          </div>

          <div v-if="groupLoading" class="tile-loading h-v-centerer">
            <img :src="shizenLogo" alt="">
          </div>
        </div>
        <!-- Vitality cell -->
        <div class="group-vitality">
          <div class="content">
            <canvas ref="vitalityCanvas" />
            <div class="details">
              <div class="vitality-score">
                <span class="score" :style="vitalityScoreStyle">
                  {{ vitalityChartScore.sToFixed(1) }}
                </span>
                <span>
                  / 100
                </span>
              </div>
              <div class="vitality-delta">
                <span v-if="vitalityDelta > 0">
                  <Icon class="up">
                    {{ arrowUpFill }}
                  </Icon>
                  <span>{{ $tc('message.VH_DELTA_INCREASE', absVitalityDelta, { val: absVitalityDelta }) }}</span>
                </span>
                <span v-else-if="vitalityDelta < 0">
                  <Icon class="down">
                    {{ arrowDownLine }}
                  </Icon>
                  <span>{{ $tc('message.VH_DELTA_DECREASE', absVitalityDelta, { val: absVitalityDelta }) }}</span>
                </span>
                <span v-else>
                  <Icon>
                    {{ dashBold }}
                  </Icon>
                  <span>{{ $t('message.VH_DELTA_NEITHER') }}</span>
                </span>
              </div>
            </div>
            <div class="chart-icon-wrapper" @click="vitalityChartClicked">
              <Icon>
                {{ barChartMedium }}
              </Icon>
            </div>
          </div>
          <div v-if="!isCategory" class="cell-title">
            {{ $t('message.VITALITY_OVERALL') }}
          </div>
        </div>
        <!-- End of vitality cell -->

        <!-- Criteria cell -->
        <div class="group-criteria">
          <div class="content">
            <canvas ref="criteriaCanvas" />
            <div class="details">
              <div v-for="(section, sidx) in criticalitySections" :key="sidx" class="criteria-nb-row">
                <div
                  v-for="(criticality, idx) in section"
                  :key="idx"
                  class="criteria-nb-col"
                  @click="$emit('criticalityClick', criticality, { key: 'all' })"
                >
                  <span
                    class="criteria-number"
                    :style="{ color: criticality.color }"
                  >
                    {{ criticality.criteria.length }}
                  </span>
                  <span>{{ criticality.title }}</span>
                </div>
              </div>
            </div>
            <div class="chart-icon-wrapper" @click="criteriaChartClicked">
              <Icon>
                {{ barChartMedium }}
              </Icon>
            </div>
          </div>
          <div v-if="!isCategory" class="cell-title">
            {{ $t('message.VITALITY_CRITERIA') }}
          </div>
        </div>
        <!-- End of criteria cell -->
      </div>
      <!-- End of main tile content -->

      <!-- Details display button -->
      <div v-if="!isCategory" class="see-more" @click="detailsDisplayed = !detailsDisplayed">
        <button class="add-button">
          <Icon :h="12">
            {{ wideArrowTopMedium }}
          </Icon>
          <span v-if="detailsDisplayed">{{ $t('message.VH_HIDE_DETAILS') }}</span>
          <span v-else>{{ $t('message.VH_DISPLAY_DETAILS') }}</span>
        </button>
      </div>
      <!-- End of details display button -->


      <div v-if="!isCategory" class="delete-wrapper v-centerer" @click="$emit('deleteClick')">
        <Icon>
          {{ deleteMedium }}
        </Icon>
      </div>
    </div>
    <!-- End of main content -->

    <!-- Details displayed when clicking a button -->
    <div v-if="detailsDisplayed && category === null" class="details">
      <GroupVitalityTile
        v-for="subCategory in vitalityCategories"
        :key="subCategory.key"
        :group="group"
        :category="subCategory"
        @criticalityClick="$emit('criticalityClick', $event, subCategory)"
      />
    </div>
    <!-- End of details -->

    <Modal v-model="showChart" class="period-chart" xlarge>
      <VHubChartWrapper
        :chart-scores-key="category ? category.scoresKey : 'vitalityScores'"
        :chart-title="$t('message.VH_VITALITY_OVER_LAST_PERIODS')"
        :groups="[group]"
        no-close-button
      />
    </Modal>

    <Modal v-model="showCriteriaChart" class="period-chart" xlarge>
      <VHubChartWrapper
        :chart-scores-key="category ? category.scoresKey : 'vitalityScores'"
        :chart-title="$t('message.VH_CRITERIA_OVER_LAST_PERIODS')"
        no-close-button
        use-canvas-slot
        @build="buildCriteriaBreakdown"
      >
        <canvas ref="criteriaBreakdownChart" />
      </VHubChartWrapper>
    </Modal>
  </div>
</template>

<script>
/**
 * Component displaying the vitality details of a group of teams (or single team)
 * User can set up groups of teams as he wants.
 * Default groups are: 1) His own team, 2) the teams he directly manages.
 *
 * This component is also used to display details of a specific category within a group.
 * In that case, the 'category' prop will be specified and != null.
 *
 * @vue-prop {Object} group - Group whose details must be displayed in this component
 * @vue-prop {Object} [category=null] - Category in VitalityCategory.all
 */

import { Chart } from 'chart.js';
import { isEqual, cloneDeep } from 'lodash';
// Vuex
import { createNamespacedHelpers } from 'vuex';
// Components
import EditableLabel from '@/components/editable-label.vue';
import Icon from '@/components/icon.vue';
import Modal from '@/components/modal.vue';
import VHubChartWrapper from '@/components/vitality-hub/chart-wrapper.vue';
// Utils
import { VitalityCategory } from '@/utils/classes/VitalityCategory';
import { decodeHTML, shizenLogo } from '@/utils/main';
import { vitalityThresholds } from '@/utils/vhub-utils';
import {
  arrowDownLine,
  arrowUpFill,
  barChartMedium,
  dashBold,
  deleteMedium,
  wideArrowTopMedium,
} from '@/assets/icons';
import { VitalityLoadingState } from '@/utils/classes/VitalityLoadingState';

const VLS = VitalityLoadingState;
const { mapGetters, mapState } = createNamespacedHelpers('vitality');


export default {
  name: 'GroupVitalityTile',

  components: {
    EditableLabel,
    Icon,
    Modal,
    VHubChartWrapper,
  },

  props: {
    group: {
      type: Object,
      required: true,
    },
    category: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      // Icons
      arrowDownLine,
      arrowUpFill,
      barChartMedium,
      dashBold,
      deleteMedium,
      wideArrowTopMedium,
      shizenLogo,
      nameKey: 1,
      // Charts
      vitalityChart: null,
      criteriaChart: null,
      periodsCriteriaChart: null,
      genericChartOptions: {
        responsive: false,
        cutout: '73%',
        animation: { duration: 0 },
        tooltips: { enabled: false },
        legend: { display: false },
        elements: {
          arc: { roundedCornersFor: 0 },
        },
        layout: { padding: { left: 0, right: 1, top: 0, bottom: 0 } },
      },
    };
  },

  computed: {
    /** Boolean: Whether this component is a sub-item of a group tile */
    isCategory() {
      return this.category !== null;
    },
    /** Object: CSS style of $el */
    elClasses() {
      return {
        'details-displayed': this.detailsDisplayed,
        'is-category': this.isCategory,
      };
    },
    /** String: Title of the tile in the left cell */
    tileTitle() {
      return !this.isCategory
        ? this.group.name
        : this.$t(`message.${this.category.titleTr}`);
    },
    /** String: Display the number and names of the teams */
    groupTeams() {
      if (this.isCategory) {
        return '';
      }
      const { teams } = this.group;
      return `(${this.$tc('message.N_TEAMS', teams.length)}) \
        ${teams.map(team => decodeHTML(team.name)).join(', ')}`;
    },
    /** Array: Array of scores to consider. If 'category' prop is specified, scores of that category */
    vitalityScores() {
      return this.isCategory ? this.group[this.category.scoresKey] : this.group.vitalityScores;
    },
    /** Number: Vitality score. If 'category' prop is specified, score of that category */
    vitalityChartScore() {
      return this.vitalityScores ? this.vitalityScores[0] || 0 : 0;
    },
    /** Object: Threshold corresponding to the score of the period */
    vitalityChartThreshold() {
      for (let i = 0, len = vitalityThresholds.length; i < len; i++) {
        if (this.vitalityChartScore <= vitalityThresholds[i].score) {
          return vitalityThresholds[i];
        }
      }
      return vitalityThresholds[0];
    },
    /** Number: Difference between the vitality score of the current period vs last period */
    absVitalityDelta() {
      return Math.abs(this.vitalityDelta);
    },
    /** Number: Difference between the vitality score of the current period vs last period */
    vitalityDelta() {
      return this.vitalityScores ? (this.vitalityScores[0] - this.vitalityScores[1]).sToFixed(1) : 0;
    },
    /** Object: Chart.js config of the vitality chart */
    vitalityChartConfig() {
      const { color, lightColor } = this.vitalityChartThreshold;
      return {
        type: 'doughnut',
        data: {
          labels: [],
          datasets: [{
            data: [this.vitalityChartScore, 100 - this.vitalityChartScore],
            backgroundColor: [color, lightColor],
            hoverBackgroundColor: [color, lightColor],
            borderWidth: [0, 0],
            hoverBorderWidth: [0, 0],
          }],
        },
        options: this.genericChartOptions,
      };
    },
    /** Object: Style applied to the score */
    vitalityScoreStyle() {
      return {
        color: this.vitalityChartThreshold.color,
      };
    },
    /** Array: Categories whose details should be displayed in this tile */
    vitalityCategories() {
      return this.isCategory ? [this.category] : VitalityCategory.all;
    },
    /**
     * Array: Array of 4 objects. The first objects refer to the critical criteria,
     * the 2nd to the inadequate criteria, 3rd acceptable criteria, 4th good criteria
     * [{ title: 'To analyse', criteria: [{}, {}] }]
     */
    criteriaPerCriticality() {
      const len = vitalityThresholds.length;
      const output = cloneDeep(vitalityThresholds)
        .map(t => ({ ...t, title: this.$t(t.titleTr), criteria: [] }));
      // Check all criteria within all categories
      this.vitalityCategories.forEach((category) => {
        category.criteria.forEach((criterion) => {
          for (let i = 0; i < len; i++) {
            if (
              this.group[criterion.scoresKey]
              && this.group[criterion.scoresKey][0].score <= vitalityThresholds[i].score
            ) {
              output[i].criteria.push(criterion);
              break;
            }
          }
        });
      });
      return output;
    },
    /** Array: criteriaPerCriticality split into two sections */
    criticalitySections() {
      if (!this.criteriaPerCriticality) {
        return [];
      }
      return [
        [this.criteriaPerCriticality[0], this.criteriaPerCriticality[1]],
        [this.criteriaPerCriticality[2], this.criteriaPerCriticality[3]],
      ];
    },
    criticalityBreakdown() {
      return this.criteriaPerCriticality.map(c => c.criteria.length);
    },
    /** Object: Chart.js config of the criteria chart */
    criteriaChartConfig() {
      if (!this.criteriaPerCriticality) {
        return { options: this.genericChartOptions };
      }
      const data = this.criticalityBreakdown;
      const colors = vitalityThresholds.map(t => t.color);
      return {
        type: 'doughnut',
        data: {
          labels: [],
          datasets: [{
            data,
            backgroundColor: colors,
            hoverBackgroundColor: colors,
            borderWidth: [0, 0, 0, 0],
            hoverBorderWidth: [0, 0, 0, 0],
          }],
        },
        options: this.genericChartOptions,
      };
    },
    detailsDisplayedQueryParam() {
      return `vhGroup${this.category ? this.category.key : 'Vitality'}DetailsDisplayed${this.group.key}`;
    },
    showChartQueryParam() {
      return `vhGroup${this.category ? this.category.key : 'Vitality'}ChartDisplayed${this.group.key}`;
    },
    showCriteriaChartQueryParam() {
      return `vhGroup${this.category ? this.category.key : 'Vitality'}CriteriaChartDisplayed${this.group.key}`;
    },
    /** Boolean: Is the current group loading any kind of data or XHR */
    groupLoading() {
      return this.loadingPerGroup[this.group.key]
        ? Object.values(this.loadingPerGroup[this.group.key]).some(l => [true, VLS.FETCHING, VLS.WORKING].includes(l))
        : false;
    },
    ...mapGetters(['loading']),
    ...mapState(['ranges', 'loadingPerGroup']),
    // ============
    // Query params
    // ============
    /** Boolean: Should the details be displayed */
    detailsDisplayed: {
      get() {
        return this.$route.query[this.detailsDisplayedQueryParam] === 'true';
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        query[this.detailsDisplayedQueryParam] = newVal ? 'true' : undefined;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Boolean: Should the vitality chart be displayed */
    showChart: {
      get() {
        return this.$route.query[this.showChartQueryParam] === 'true';
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        query[this.showChartQueryParam] = newVal ? 'true' : undefined;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
    /** Boolean: Should the criteria chart be displayed */
    showCriteriaChart: {
      get() {
        return this.$route.query[this.showCriteriaChartQueryParam] === 'true';
      },
      set(newVal) {
        const query = Object.assign({}, this.$route.query);
        query[this.showCriteriaChartQueryParam] = newVal ? 'true' : undefined;
        if (!isEqual(query, this.$route.query)) {
          this.$router.replace({ query });
        }
      },
    },
  },

  watch: {
    group: {
      deep: true,
      handler() {
        this.nameKey++;
      },
    },
    loading(isLoading) {
      if (!isLoading) {
        if (!this.vitalityChart) {
          this.drawVitalityChart();
        }
        if (!this.criteriaChart) {
          this.drawCriteriaChart();
        }
      }
    },
    vitalityChartScore(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.drawVitalityChart();
      }
    },
    criticalityBreakdown(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.drawCriteriaChart();
      }
    },
  },

  mounted() {
    // For hot-reload purpose
    this.drawVitalityChart();
    this.drawCriteriaChart();
  },

  beforeDestroy() {
    if (this.vitalityChart) {
      this.vitalityChart.destroy();
    }
    if (this.criteriaChart) {
      this.criteriaChart.destroy();
    }
    if (this.periodsCriteriaChart) {
      this.periodsCriteriaChart.destroy();
    }
  },

  methods: {
    decodeHTML,

    /** Re-draw the doughnut chart for the group's vitality */
    drawVitalityChart() {
      if (this.vitalityChart) {
        this.vitalityChart.destroy();
        this.vitalityChart = null;
      }
      const canvas = this.$refs.vitalityCanvas;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      if (this.vitalityChartConfig && !this.loading) {
        this.vitalityChart = new Chart(canvas.getContext('2d'), this.vitalityChartConfig);
      }
    },
    /** Re-draw the doughnut chart for the group's vitality */
    drawCriteriaChart() {
      if (this.criteriaChart) {
        this.criteriaChart.destroy();
        this.criteriaChart = null;
      }
      const canvas = this.$refs.criteriaCanvas;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      if (this.criteriaChartConfig && !this.loading) {
        this.criteriaChart = new Chart(canvas.getContext('2d'), this.criteriaChartConfig);
      }
    },
    /** Display the vitality trend */
    vitalityChartClicked() {
      this.showChart = true;
    },
    /** Display the criteria breakdown trend */
    criteriaChartClicked() {
      this.showCriteriaChart = true;
    },
    /** Build event triggered from chart-wrapper.vue => create the criteria breakdown chart */
    buildCriteriaBreakdown(labels) {
      const canvas = this.$refs.criteriaBreakdownChart;
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      const twoDots = this.$t('message.TWO_DOTS');

      // Calculate breakdown for each period
      const datasets = [];
      const rangesLength = labels.length;
      const jlen = vitalityThresholds.length;
      for (let j = 0; j < jlen; j++) {
        const threshold = vitalityThresholds[j];
        datasets.push({
          type: 'bar',
          label: this.$t(threshold.titleTr),
          data: new Array(rangesLength).fill(0),
          customData: new Array(rangesLength).fill().map(() => ({ criteria: [] })),
          borderWidth: 2,
          borderColor: threshold.color,
          backgroundColor: threshold.lightColor,
        });
      }

      for (let i = 0; i < rangesLength; i++) {
        this.vitalityCategories.forEach((category) => {
          category.criteria.forEach((criterion) => {
            const { scoresKey } = criterion;
            for (let j = 0; j < jlen; j++) {
              if (this.group[scoresKey] && this.group[scoresKey][i].score <= vitalityThresholds[j].score) {
                const idx = rangesLength - 1 - i;
                datasets[j].data[idx]++;
                datasets[j].customData[idx].criteria.push(criterion);
                break;
              }
            }
          });
        });
      }

      const config = {
        data: { labels, datasets },
        options: {
          scales: {
            yAxes: [{
              stacked: true,
            }],
            xAxes: [{
              stacked: true,
              offset: true,
            }],
          },
          tooltips: {
            callbacks: {
              title: ([item]) => {
                const criticality = datasets[item.datasetIndex].label;
                return `${item.label}${twoDots}${item.value} ${criticality}`;
              },
              label: (item) => {
                const { criteria } = datasets[item.datasetIndex].customData[item.index];
                // return criteria.map(crit => this.$t(`message.${crit.titleTr}`)).join('<br>');
                return criteria.reduce((output, crit) => `${output}- ${this.$t(`message.${crit.titleTr}`)}<br>`, '');
              },
            },
          },
        },
      };
      if (this.periodsCriteriaChart) {
        this.periodsCriteriaChart.destroy();
      }
      this.periodsCriteriaChart = new Chart(canvas.getContext('2d'), config);
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.group-vitality-tile {
  $border-color: #cccccc;

  .main-tile {
    display: flex;

    > .tile-wrapper {
      border: 1px solid $border-color;
      display: flex;
      flex: 0 0 calc(85% - 2.1em);
      background: white;
      transition: box-shadow 0.1s;
      min-width: 0px;

      .group-name {
        display: flex;
        flex-flow: column;
        align-items: center;
        justify-content: center;
        flex: 0 0 20%;
        padding: 0px 0.5em;
        text-align: center;
        box-sizing: border-box;
        position: relative;
        min-width: 0px;

        .editable-label .editable-label-text {
          max-height: 4.3em;
        }
        .group-teams {
          padding: 0.2em;
          font-size: 0.9em;
          font-style: italic;
          white-space: nowrap;
          text-overflow: ellipsis;
          overflow: hidden;
          cursor: pointer;
          max-width: 100%;

          &:hover {
            background: $color-hoverbtn-light-blue;
          }
        }
        .tile-loading {
          position: absolute;
          top: 0px;
          left: 0px;
          bottom: 0px;
          right: 0px;
          background: white;

          img {
            width: 3em;
            height: 3em;
            animation: rotation 2s infinite linear;
          }
        }
      }
      .group-criteria, .group-vitality {
        border-left: 1px solid $border-color;
        flex: 0 0 40%;
        display: flex;
        flex-flow: column;
        box-sizing: border-box;

        > .content {
          flex: 1;
          display: flex;

          > canvas {
            width: 2.5em;
            height: 2.5em;
            margin: 0.5em 0.5em 0.5em 1em;
          }
          > .details {
            flex: 1;
          }
          > .chart-icon-wrapper {
            width: 1.1em;
            height: 1.1em;
            margin: 0.2em;
            padding: 0.4em;
            background: #e0e0e0;
            border-radius: 3em;
            cursor: pointer;

            &:hover {
              background: #d8d8d8;
            }

            svg {
              width: 100%;
              height: 100%;
            }
          }
        }
        .cell-title {
          height: 1.4em;
          background: #eee;
          text-align: center;
          padding: 0.2em;
          border-top: 1px solid $border-color;
        }
      }
      .group-vitality {
        > .content > .details {
          display: flex;
          flex-flow: column;
          align-items: center;
          justify-content: center;
        }
        .vitality-score {
          .score {
            font-weight: 550;
          }
        }
        .vitality-delta {
          display: flex;
          align-items: center;
          text-align: center;

          svg {
            width: 0.6em;
            height: auto;
            margin-right: 0.4em;

            &.up {
              color: rgb(4, 142, 43),
            }
            &.down {
              color: rgb(241, 49, 34),
            }
          }
        }
      }
      .group-criteria {
        min-width: 0;

        .details {
          display: flex;
          align-items: center;
          min-width: 0;

          .criteria-nb-row {
            display: flex;
            flex-flow: column;
            flex: 0 0 50%;
            min-width: 0;

            .criteria-nb-col {
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              padding: 1px 3px;
              cursor: pointer;

              &:hover {
                background: $color-hoverbtn-light-blue;
              }

              .criteria-number {
                font-weight: 550;
                margin-right: 0.2em;
              }
            }
          }
        }
      }
    }

    > .see-more {
      display: flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 15%;
      cursor: pointer;

      &:hover {
        background: $color-hoverbtn-light-blue;
      }
      button {
        color: $color-blue-text;
        padding: 0px 0.4em;

        svg {
          width: 1.4em;
          height: auto;
          transform: rotate(180deg);
        }
      }
    }

    .delete-wrapper {
      padding: 0.2em 0.3em;
      cursor: pointer;

      &:hover {
        background: $color-hoverbtn-red;
      }
      svg {
        width: 1.5em;
        height: auto;
      }
    }
  }

  .period-chart {
    display: flex;
    flex-flow: column;

    .vhub-chart-wrapper {
      .title {
        margin: 0.5em 0;
      }
    }
  }

  &.details-displayed {
    > .main-tile {
      z-index: 1;
      position: relative;

      .tile-wrapper {
        box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.15);
      }

      > .see-more button svg {
        transform: rotate(0deg);
      }
    }
  }
}

@media (max-width: $smartphone-landscape) {
  .group-vitality-tile {
    .main-tile {
      > .tile-wrapper {
        // 2.1em = delete-wrapper ; 2.2em = svg width + padding
        flex: 0 0 calc(100% - 4.3em);
      }
      > .see-more {
        flex: 0 0;

        button {
          span {
            display: none;
          }
          svg {
            margin: 0;
          }
        }
      }
    }
  }
}



@media (max-width: $smartphone-portrait) {
  .group-vitality-tile {
    .main-tile > .tile-wrapper {
      .group-criteria, .group-vitality {
        > .content > canvas {
          margin: 0.2em 0.2em 0.2em 0.5em;
        }
      }
      .group-vitality {
        .vitality-score {
          span:not(.score) {
            display: none;
          }
        }
        .vitality-delta {
          display: none;
        }
      }
    }
  }
}

</style>
